import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { fetchOverview } from './api';
import { PLATFORM_CSS } from './platformStyles';
import { useAdminIdleTimeout } from './useAdminIdleTimeout';

const PlatformLayout: React.FC = () => {
  const { token, userEmail, logout } = useAuth();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({ trials: 0, billing: 0, support: 0 });

  const handleIdleTimeout = useCallback(() => {
    toast.info('Signed out — platform session expired after 15 minutes of inactivity.');
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useAdminIdleTimeout(handleIdleTimeout);

  useEffect(() => {
    if (!token) return;
    fetchOverview(token)
      .then((d) => setBadges({
        trials: d.trialsExpiringSoon || 0,
        billing: d.businesses?.payment_issues || 0,
        support: d.support?.billing_open || 0,
      }))
      .catch(() => {});
  }, [token]);

  const nav = (to: string, label: string, badge?: number, end = false) => (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? 'active' : '')}>
      <span>{label}</span>
      {badge != null && badge > 0 ? <span className="plat-badge">{badge}</span> : null}
    </NavLink>
  );

  const section = (title: string) => (
    <div className="plat-nav-section">{title}</div>
  );

  return (
    <div className="plat-page">
      <style>{PLATFORM_CSS}</style>
      <ToastContainer position="top-right" autoClose={2500} theme="dark" />
      <div className="plat-shell">
        <aside className="plat-sidebar">
          <div className="plat-brand">
            <h1>Shaho Platform</h1>
            <p>Super Admin</p>
          </div>
          <nav className="plat-nav">
            {section('Platform')}
            {nav('/admin', 'Overview', undefined, true)}
            {nav('/admin/applications', 'Applications', badges.trials)}
            {nav('/admin/businesses', 'Businesses')}
            {nav('/admin/bookings', 'Demo Bookings')}
            {nav('/admin/billing', 'Billing & Support', badges.billing + badges.support)}

            {section('GDPR & Compliance')}
            {nav('/admin/gdpr?tab=exports', 'Data Exports (DSAR)')}
            {nav('/admin/gdpr?tab=erasure', 'Erasure Requests')}
            {nav('/admin/gdpr?tab=access', 'Access Logs')}
            {nav('/admin/gdpr?tab=policy', 'Retention Policy')}

            {section('System')}
            {nav('/admin/system?tab=health', 'System Health')}
            {nav('/admin/system?tab=email', 'Email Queue')}
            {nav('/admin/system?tab=access', 'Access Cases')}
            {nav('/admin/system?tab=logs', 'Audit Logs')}

            {section('Admin')}
            {nav('/admin/system?tab=security', 'Security (2FA)')}
          </nav>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 12, marginTop: 8 }}>
            <div className="plat-muted" style={{ padding: '0 12px 8px', fontSize: 11 }}>{userEmail}</div>
            <button
              type="button"
              className="plat-btn plat-btn-ghost"
              style={{ width: '100%' }}
              onClick={() => { logout(); navigate('/login'); }}
            >
              Sign out
            </button>
          </div>
        </aside>
        <main className="plat-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PlatformLayout;
